import React, { FC, memo, useMemo } from 'react'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Container } from '@ucheba/ui/components/Container/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { LineParam } from '@ucheba/ui/components/LineParam/desktop'
import { updateSticky } from '@ucheba/ui/components/Sticky'
import { LimitedList } from '@ucheba/ui/components/LimitedList/desktop'
import { useSelector } from 'react-redux'
import { authSelectors } from '@ucheba/store/auth'
import { Aside } from '@ucheba/ui/components/Aside/desktop'
import { useRouter } from 'next/router'
import { analyticEvent } from '@ucheba/utils/helpers/analytics/events'
import {
  EAnalyticEvents,
  EAnalyticEventTargets,
  EAnalyticSections,
} from '@ucheba/utils/helpers/analytics/events/types'
import { plannerSpecialtiesSelectors } from '@ucheba/store/planner/specialties'
import { useWantToStudy } from '../../../../../components/LayoutInstitution/components/WantToStudy/bll/useWantToStudy'
import EducationalInstitutionAddedNotification from '../../../../../components/LayoutInstitution/components/WantToStudy/components/EducationalInstitutionAddedNotification/desktop'
import WantToStudy from '../../../../../components/LayoutInstitution/components/WantToStudy/desktop'
import { Banner } from '../../../../../components/Banner/desktop'
import { ISpecialityIdPageProps } from '../types'
import SC from '../base/SpecialityIdPage.styles'
import { ExamsList } from '../../../../../components/ExamsList/desktop'
import { OlympiadsList } from '../../../../../components/OlympiadsList/desktop'
import { SpecialitiesList } from '../../../../../components/SpecialitiesList/desktop'
import { InstitutionsList } from '../../../../../components/InstitutionsList/desktop'
import { ProfessionsList } from '../../../../../components/ProfessionsList/desktop'
import { useSpecialityHeaderInfo, useSpecialityIdPageCore } from '../bll'
import LidMagnetForm from '../../../../../components/LidMagnetForm/desktop'
import {
  EApplicationFormProductTypes,
  EApplicationFunnels,
} from '../../../../../components/AppliactionForm/types'
import { ETypeRequest } from '../../../ege/index/types'

const { ShortInfo } = SC

/** Страница специальности */
const SpecialityIdPage: FC<ISpecialityIdPageProps> = ({ content }) => {
  const {
    id,
    name,
    specificity,
    career,
    specialization,
    trainingFormTypes,
    specialityType,
    institutionsCount,
    minPassingScore,
    maxPassingScore,
    budgetPlacesCount,
    avgInstitutionsCountForAll,
    avgMinPassingScoreForAll,
    avgMaxPassingScoreForAll,
    avgBudgetPlacesCountForAll,
    maxBudgetPlacesCountForAll,
    maxInstitutionsCountForAll,
    maxPassingScoreForAll,
    examSetList,
    olympiads,
    institutions,
    professions,
    similar,
  } = content

  const { query } = useRouter()

  const isAuth = useSelector(authSelectors.isAuth)
  const {
    trainingForms,
    specializationData,
    isForInstitution,
    getLinkProps,
    isCollage,
    onClickGetMoreInstitutions,
    totalInstitutions,
    totalSimilar,
    onClickGetMoreSimilar,
    hrefBySpecialty,
  } = useSpecialityIdPageCore({
    trainingFormTypes,
    specialization,
    specialityType,
  })

  const { entityIds } = useSelector(plannerSpecialtiesSelectors.entitySpecialitiesIds)
  const {
    handleWantToStudy,
    isInPlanOfAdmission,
    openSpecialityAdded,
    handleToggleSpecialityAdded,
  } = useWantToStudy({
    id,
    ids: entityIds || [],
    type: 'specialities',
  })

  const title = useSpecialityHeaderInfo(content)

  const params = useMemo(() => {
    if (query['programTypes[]']) {
      return query
    }

    return {
      ...query,
      programTypes: [specialityType.value],
    }
  }, [query, specialityType])

  return (
    <Container>
      <Spacing />

      <Grid as='main'>
        <GridCol uxs={18} as='article'>
          <ShortInfo>
            {/* Андрей сказал, что пока не выводим направление
            <Link href={direction.href} passHref>
              {direction.name}</a>
            </Link>
            */}

            <Text color='gray60' firstLetterUppercase>
              {title}
            </Text>
          </ShortInfo>

          <Heading h1={`Специальность ${name}`} />

          {specificity && <Text>{specificity}</Text>}

          {career && (
            <>
              <Spacing spacing='small' />

              <Text>{career}</Text>
            </>
          )}

          <Spacing spacing='small' />

          {specializationData && (
            <>
              <Text fontWeight='bold'>{specializationData}</Text>

              <Spacing spacing='small' />
            </>
          )}

          <Text fontWeight='bold'>{trainingForms}</Text>

          <Spacing spacing='xlarge' />

          <Grid spacing='xxlarge'>
            <GridCol uxs={8}>
              <Heading
                h3={isForInstitution ? 'Вузов' : 'Колледжей'}
                as='h2'
                spacing='xsmall'
              />

              <LineParam
                label='По этой специальности'
                fontWeight='semibold'
                currentMaxValue={institutionsCount}
                maxValue={maxInstitutionsCountForAll}
              />

              <Spacing spacing='xxsmall' />

              <LineParam
                label='В среднем по другим'
                currentMaxValue={avgInstitutionsCountForAll}
                maxValue={maxInstitutionsCountForAll}
                color='gray25'
              />
            </GridCol>

            {(Boolean(minPassingScore) || Boolean(maxPassingScore)) && (
              <GridCol uxs={8}>
                <Heading h3='Проходной балл ' as='h2' spacing='xsmall' />

                <LineParam
                  label='На эту специальность'
                  fontWeight='semibold'
                  currentMinValue={minPassingScore}
                  currentMaxValue={maxPassingScore}
                  maxValue={maxPassingScoreForAll}
                />

                <Spacing spacing='xxsmall' />

                <LineParam
                  label='В среднем на другие'
                  currentMinValue={avgMinPassingScoreForAll}
                  currentMaxValue={avgMaxPassingScoreForAll}
                  maxValue={maxPassingScoreForAll}
                  color='gray25'
                />
              </GridCol>
            )}

            <GridCol uxs={8}>
              <Heading h3='Бюджетных мест' as='h2' spacing='xsmall' />

              <LineParam
                label='На эту специальность'
                fontWeight='semibold'
                currentMaxValue={budgetPlacesCount}
                maxValue={maxBudgetPlacesCountForAll}
              />

              <Spacing spacing='xxsmall' />

              <LineParam
                label='В среднем на другие'
                currentMaxValue={avgBudgetPlacesCountForAll}
                maxValue={maxBudgetPlacesCountForAll}
                color='gray25'
              />
            </GridCol>
          </Grid>

          <Spacing spacing='uxlarge' />

          {isCollage ? (
            <LidMagnetForm
              title='Хочешь сдать ОГЭ на&nbsp;5+ и&nbsp;поступить в&nbsp;колледж на&nbsp;бюджет?'
              text='Поможем подготовиться к&nbsp;ОГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
              buttonText='Подготовиться к ОГЭ'
              size='small'
              funnel={EApplicationFunnels.speciality_main}
              productType={EApplicationFormProductTypes.oge}
              requestTarget={ETypeRequest.oge_lead_request}
              onSubmitForm={() => {
                analyticEvent({
                  targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                  eventName: EAnalyticEvents.leadFormRequestSpeciality,
                  section: EAnalyticSections.leadMagnetForm,
                })
              }}
              list={[
                'Расскажем о системе эффективной подготовки',
                'Познакомим с онлайн-школой и платформой',
                'Ответим на вопросы и запишем на занятия',
              ]}
            />
          ) : (
            <LidMagnetForm
              title='Хочешь сдать ЕГЭ на&nbsp;80+ и&nbsp;поступить в&nbsp;вуз на&nbsp;бюджет?'
              text='Поможем подготовиться к&nbsp;ЕГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
              buttonText='Подготовиться к ЕГЭ'
              size='small'
              funnel={EApplicationFunnels.speciality_main}
              productType={EApplicationFormProductTypes.ege}
              requestTarget={ETypeRequest.ege_lead_request}
              onSubmitForm={() => {
                analyticEvent({
                  targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                  eventName: EAnalyticEvents.leadFormRequestSpeciality,
                  section: EAnalyticSections.leadMagnetForm,
                })
              }}
              list={[
                'Расскажем о системе эффективной подготовки',
                'Познакомим с онлайн-школой и платформой',
                'Ответим на вопросы и запишем на занятия',
              ]}
            />
          )}

          {examSetList.length > 0 && (
            <>
              <Spacing spacing='uxlarge' />

              <Heading h2='С какими ЕГЭ можно поступить' spacing='large' />

              <LimitedList
                items={examSetList}
                limit={3}
                onShowAll={updateSticky}
                buttonText='Показать все варианты ЕГЭ'
                id='egeVariants'
                isAuth={isAuth}
                showAllButtonLinkIsLink={false}
              >
                {({ limitedItems }) => (
                  <ExamsList id={id} items={limitedItems} getLinkProps={getLinkProps} />
                )}
              </LimitedList>
            </>
          )}

          {/* {specialities.length > 0 && (
            <>
              <Spacing spacing='uxlarge' />

              <Heading h2='Профили обучения' spacing='large' />

              <SpecialitiesList items={specialities} />
            </>
          )} */}

          <Spacing spacing='uxlarge' />

          <Banner id={2} extraParams={params} />

          {institutions.length > 0 && (
            <>
              <Spacing spacing='uxlarge' />

              <Heading
                h2={`${isForInstitution ? 'Вузы' : 'Колледжи'} по специальности`}
                spacing='large'
              />

              <LimitedList
                items={institutions}
                limit={5}
                onShowAll={updateSticky}
                buttonText={`Показать все ${isForInstitution ? 'вузы' : 'колледжи'}`}
                id='institutions'
                isAuth={isAuth}
                redirect={hrefBySpecialty}
                total={totalInstitutions}
                showAllButtonLinkIsLink={false}
              >
                {({ limitedItems }) => (
                  <InstitutionsList specialityId={id} items={limitedItems} />
                )}
              </LimitedList>
            </>
          )}

          {olympiads.length > 0 && (
            <>
              <Spacing spacing='uxlarge' />

              <Heading h2='Поступление по олимпиаде' spacing='large' />

              <OlympiadsList items={olympiads} limit={3} onShowAll={updateSticky} />
            </>
          )}

          {professions.length > 0 && (
            <>
              <Spacing spacing='uxlarge' />

              <Heading h2='Профессии' spacing='large' />

              <ProfessionsList items={professions} />
            </>
          )}

          {similar.length > 0 && (
            <>
              <Spacing spacing='uxlarge' />

              <Heading h2='Похожие специальности' spacing='large' />

              <LimitedList
                items={similar}
                limit={3}
                onShowAll={onClickGetMoreSimilar}
                buttonText='Показать все специальности'
                id='specialities'
                isAuth={isAuth}
                total={totalSimilar}
                showAllButtonLinkIsLink={false}
              >
                {({ limitedItems }) => <SpecialitiesList items={limitedItems} />}
              </LimitedList>
            </>
          )}
        </GridCol>

        <GridCol uxs={6} as='aside'>
          <Spacing spacing='medium' />

          <div style={{ position: 'relative', height: '100%', marginLeft: '32px' }}>
            <div style={{ position: 'sticky', top: '32px' }}>
              <WantToStudy
                isInPlanOfAdmission={isInPlanOfAdmission}
                handleWantToStudy={handleWantToStudy}
                block
                alternativeButtonBlock
                type='specialities'
                isAuth={isAuth}
                buttonText='Добавить в план'
              />

              <Spacing spacing='xlarge' />

              {/* <ConsultationPromo orientation='vertical' /> */}

              <Banner id={3} extraParams={params} />
            </div>
          </div>
        </GridCol>
      </Grid>

      <Spacing spacing='uxlarge' />

      <Banner id={6} extraParams={params} />

      <Banner id={14} extraParams={params} />

      <EducationalInstitutionAddedNotification
        lsKey='specialityInstitutionAddedNotification'
        open={openSpecialityAdded}
        title='Сециальность добавлена <br/> в план поступления'
        onClose={handleToggleSpecialityAdded}
      />
    </Container>
  )
}

export default memo(SpecialityIdPage)
